import * as React from 'react'
import { ChevronRight, Loader2 } from 'lucide-react'
import {
  useActiveBranchRecord,
  useListFlaggedNodesQuery,
  useRecordNodeEvidenceMutation,
  type FlaggedNode
} from '@axilon-apps/domains-ontology-ui-state'

import { useProtectedBranchGuard } from '../../../components/Branches/ProtectedBranchPromptDialog'

import { Badge } from '../../../components/ui/badge'
import { Card, CardContent } from '../../../components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../../components/ui/collapsible'
import { formatRelativeTime } from '../../../lib/time'
import { EntityReviewRow } from './EntityReviewRow'

/** Group flagged nodes by their primary label, groups sorted alphabetically. */
function groupByLabel(nodes: FlaggedNode[]): Array<{ label: string; nodes: FlaggedNode[] }> {
  const byLabel = new Map<string, FlaggedNode[]>()
  for (const node of nodes) {
    const label = node.labels[0] ?? 'Other'
    const bucket = byLabel.get(label)
    if (bucket) bucket.push(node)
    else byLabel.set(label, [node])
  }
  return [...byLabel.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, grouped]) => ({ label, nodes: grouped }))
}

function FlaggedReason({ node }: { node: FlaggedNode }): React.ReactElement | null {
  if (!node.reason) return null
  return (
    <div className="rounded-md border border-st-down/30 bg-st-down-bg/20 px-3 py-2">
      <p className="m-0 whitespace-pre-wrap text-xs text-ink-2">{node.reason}</p>
      {(node.flagged_by ?? node.flagged_at) && (
        <p className="m-0 mt-1 text-10 uppercase tracking-wide text-ink-3">
          {node.flagged_by ? `by ${node.flagged_by}` : ''}
          {node.flagged_by && node.flagged_at ? ' · ' : ''}
          {node.flagged_at ? formatRelativeTime(node.flagged_at) : ''}
        </p>
      )}
    </div>
  )
}

/**
 * The home for `valid = false` nodes. Invalidation is otherwise
 * write-only — easy to set in chat, invisible to find later — so this
 * card lists every flagged node with the operator's reason (from the
 * latest manual provenance evidence) and offers in-place re-validation.
 * Re-validating writes `valid = true` plus a manual "fixed" note through
 * the record-evidence endpoint; the node drops off this list and agents
 * trust it again on their next query.
 */
export function FlaggedNodesCard() {
  const { data: nodes, isLoading, error } = useListFlaggedNodesQuery()
  const [recordEvidence, recordState] = useRecordNodeEvidenceMutation()
  const branchGuard = useProtectedBranchGuard(useActiveBranchRecord())

  // One inline re-validate composer at a time, keyed by node id.
  const [revalidatingId, setRevalidatingId] = React.useState<string | null>(null)
  const [note, setNote] = React.useState('')
  const [submitError, setSubmitError] = React.useState('')

  const closeComposer = () => {
    setRevalidatingId(null)
    setNote('')
    setSubmitError('')
  }

  const handleConfirm = (node: FlaggedNode) => {
    const trimmed = note.trim()
    if (!trimmed || recordState.isLoading) return
    if (!branchGuard.ensureWritable()) return
    setSubmitError('')
    recordEvidence({ nodeId: node.id, note: trimmed, valid: true })
      .unwrap()
      .then(closeComposer)
      .catch(() => setSubmitError(`Could not re-validate ${node.name ?? node.id}.`))
  }

  const flagged = nodes ?? []
  const groups = React.useMemo(() => groupByLabel(nodes ?? []), [nodes])

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-ink">Flagged nodes</h3>
          <span className="text-xs text-ink-3">
            {flagged.length} {flagged.length === 1 ? 'node' : 'nodes'}
          </span>
        </div>
        <p className="m-0 text-xs leading-relaxed text-ink-3">
          Nodes marked not valid. Agents avoid citing them until someone re-validates. If the
          underlying issue is fixed, re-validate with a short note — the node is trusted again on
          the next agent query.
        </p>

        {error ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            Failed to load flagged nodes.
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
            <Loader2 className="size-3.5 animate-spin" /> Loading flagged nodes…
          </div>
        ) : flagged.length === 0 && !error ? (
          <p className="m-0 py-1 text-xs text-ink-3">
            No flagged nodes — nothing is marked invalid.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {groups.map((group) => (
              <Collapsible key={group.label} defaultOpen>
                <CollapsibleTrigger className="group flex w-full cursor-pointer items-center gap-2 rounded-md px-1 py-1.5 text-left hover:bg-bg-sunk">
                  <ChevronRight
                    aria-hidden
                    className="size-3.5 shrink-0 text-ink-3 transition-transform duration-150 ease-out group-data-[state=open]:rotate-90"
                  />
                  <span className="text-xs font-semibold text-ink">{group.label}</span>
                  <Badge variant="outline">{group.nodes.length}</Badge>
                </CollapsibleTrigger>
                <CollapsibleContent className="flex flex-col gap-2 pt-1.5">
                  {group.nodes.map((node) => (
                    <EntityReviewRow
                      key={node.id}
                      title={node.name ?? node.id}
                      subtitle={node.path}
                      badges={node.labels.filter((l) => l !== group.label)}
                      reason={<FlaggedReason node={node} />}
                      primaryLabel="Re-validate"
                      composerLabel="What changed? Your note is recorded on the node."
                      notePlaceholder="e.g. sensor replaced last week; readings verified against the counter…"
                      confirmLabel="Confirm re-validate"
                      confirmingLabel="Re-validating…"
                      isComposing={revalidatingId === node.id}
                      note={note}
                      onNoteChange={setNote}
                      isSubmitting={recordState.isLoading && revalidatingId === node.id}
                      submitError={revalidatingId === node.id ? submitError : ''}
                      onOpenPrimary={() => {
                        setRevalidatingId(node.id)
                        setNote('')
                        setSubmitError('')
                      }}
                      onCancelPrimary={closeComposer}
                      onConfirmPrimary={() => handleConfirm(node)}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        )}
      </CardContent>
      {branchGuard.dialog}
    </Card>
  )
}
